<template>
	<div>
		<auth-container v-if="$route.name === 'ReimbursementEdit'" :page="page">
			<b-toast id="practice-saved" variant="success" solid title="Changes saved" toaster="b-toaster-top-center">
				Your practice has been saved.
			</b-toast>

			<h2 class="mb-4">Reimbursement Request {{item.reimbursementNumber}}</h2>

			<b-tabs v-model="page.tabIndex" content-class="mt-4" class="page-nav-tabs">
				<b-tab title="Overview" active>
					<b-row>
						<b-col md="7">
							<b-table-simple small class="table-definitions">
								<b-tbody>
									<b-tr>
										<b-th>Proposal</b-th>
										<b-td>
											<router-link v-if="proposal !== null" :to="`/om/proposals/${proposal.id}`">{{proposal.proposalNumber}}</router-link>
											<span v-else>N/A</span>
										</b-td>
									</b-tr>
									<b-tr>
										<b-th>Sponsor</b-th>
										<b-td>
											<router-link :to="`/om/sponsors/${sponsor.id}`">{{sponsor.type}} {{sponsor.id}} - {{sponsor.name}}</router-link>
										</b-td>
									</b-tr>
									<b-tr>
										<b-th>Notes</b-th>
										<b-td>{{valueOrNa(item.notes, 'None')}}</b-td>
									</b-tr>
								</b-tbody>
							</b-table-simple>
						</b-col>
						<b-col md>
							<b-table-simple small class="table-definitions">
								<b-tbody>
									<b-tr>
										<b-th>Received</b-th>
										<b-td>{{item.dateReceived | date}}</b-td>
									</b-tr>
									<b-tr>
										<b-th>Recorded</b-th>
										<b-td>{{item.dateRecorded | date('ddd, MMM D, YYYY', 'None')}}</b-td>
									</b-tr>
									<b-tr>
										<b-th>Invoiced</b-th>
										<b-td>{{item.invoiceDate | date('ddd, MMM D, YYYY', 'None')}}</b-td>
									</b-tr>
								</b-tbody>
							</b-table-simple>
						</b-col>
					</b-row>

					<h3 class="mt-3">Practices</h3>
					<b-table striped responsive small show-empty :fields="activities.fields" :items="activities.items">
						<template #cell(edit)="data">
							<font-awesome-icon v-if="!isReadOnly" :icon="['fas', 'edit']" class="pointer text-primary" @click="editActivity(data.item.id)" />
						</template>
						<template #cell(dam)="data">
							<router-link :to="`/dams/${data.value.id}`">{{data.value.name}}</router-link>
						</template>
						<template #cell(practice)="data">
							<router-link :to="`/om/activities/${data.value.id}`">{{data.value.name}}</router-link>
						</template>
						<template #cell(unitsPerformed)="data">
							{{data.value | number(2)}} {{data.item.units}}
						</template>
						<template #cell(delete)="data">
							<font-awesome-icon v-if="!isReadOnly" :icon="['fas', 'times']" class="pointer text-danger" @click="deletePractice(data.item.id)" />
						</template>
						<template #empty>
							<p class="my-2">This reimbursement request does not have any practices entered.</p>
						</template>

						<template #custom-foot>
							<b-tr class="bg-light border-bottom font-weight-bold">
								<b-td colspan="5">Total</b-td>
								<b-td class="text-right">${{ sum(activities.items, 'invoicedAmount') | number(2)}}</b-td>
								<b-td class="text-right">${{ sum(activities.items, 'stateAmount') | number(2)}}</b-td>
								<b-td class="text-right">${{ sum(activities.items, 'requiredMatch') | number(2)}}</b-td>
								<b-td></b-td>
								<b-td class="text-right">${{ sum(activities.items, 'inkindMatchDeduction') | number(2)}}</b-td>
								<b-td class="text-right">${{ sum(activities.items, 'remainingRequiredMatch') | number(2)}}</b-td>
								<b-td class="text-right">${{ sum(activities.items, 'totalOmPaid') | number(2)}}</b-td>
								<b-td class="text-right">${{ sum(activities.items, 'stateAdminPayment') | number(2)}}</b-td>
								<b-td class="text-right">${{ sum(activities.items, 'totalPaid') | number(2)}}</b-td>
								<b-td class="text-right">${{ sum(activities.items, 'reappropriated') | number(2)}}</b-td>
								<b-td colspan="2"></b-td>
							</b-tr>
						</template>
					</b-table>

					<fixed-action-bar>
						<b-button type="button" variant="success" @click="createActivity" class="mr-2" v-if="!isReadOnly">Create Practice</b-button>
						<b-button type="button" variant="primary" to="print" append target="_blank" class="mr-2">Print</b-button>
						<back-button />
						<b-button v-if="isAuthorized(roleNames.admin)" type="button" variant="danger" @click="page.delete.show = true" class="ml-auto">Delete</b-button>
					</fixed-action-bar>
				</b-tab>
				<b-tab title="Edit" v-if="!isReadOnly">
					<reimbursement-form ref="reimbursementForm" :item="item" :proposal="proposal" is-update @change="changesSaved"></reimbursement-form>
				</b-tab>
				<b-tab title="Files">
					<div v-if="files.items.length > 0">
						<p>
							{{ isReadOnly ? 'View': 'Manage' }} file uploads related to this reimbursement request.
						</p>

						<b-list-group v-if="files.items.length > 0">
							<b-list-group-item class="d-flex justify-content-between align-items-center" v-for="file in files.items" :key="file.fileName">
								<a :href="file.fileName" target="_blank">{{file.description}}</a>
								<font-awesome-icon v-if="!isReadOnly" :icon="['fas', 'times']" class="pointer text-danger" @click="deleteFile(file)" />
							</b-list-group-item>
						</b-list-group>
					</div>
					<div v-else>
						<p><em>There are no files uploaded for this reimbursement request yet.</em></p>
					</div>

					<div v-if="!isReadOnly">
						<hr class="my-4" />

						<h3>Upload a File</h3>

						<error-list :errors="files.errors"></error-list>

						<b-row>
							<b-col md="6">
								<b-card class="bg-light">
									<b-form-file v-model="files.fileInput" @change="selectFile" class="mb-2"
												 accept=".pdf"
												 placeholder="Choose a file or drop it here..."
												 drop-placeholder="Drop file here..."></b-form-file>

									<b-form-input type="text" required v-model="files.upload.description" placeholder="Enter a description/label" />

									<save-button type="button" variant="primary" @click.native="uploadFile" :disabled="files.saving" class="mt-2" text="Upload"></save-button>
								</b-card>
							</b-col>
						</b-row>
					</div>
				</b-tab>
				<b-tab title="Change Log">
					<grid-view :api-url="`home/logs/OmReimbursementRequests/${itemId}`" use-filter small :fields="table.fields"
							   :default-sort="table.sort" :default-reverse="table.reverse" :default-per-page="table.itemsPerPage"
							   collection-description="change log"
							   item-name="log">
						<back-button />
					</grid-view>
				</b-tab>
			</b-tabs>

			<b-modal v-model="page.delete.show" size="md" title="Confirm delete" no-close-on-backdrop no-close-on-esc hide-header-close>
				<error-list :errors="page.delete.errors"></error-list>

				<p>
					Are you sure you want to delete this reimbursement request?
					This action is permanent and cannot be undone.
				</p>

				<div slot="modal-footer">
					<save-button type="button" :saving="page.delete.saving" @click.native="confirmDelete" text="Delete" variant="danger" />
					<b-button type="button" variant="secondary" @click="page.delete.show = false" class="ml-1">Cancel</b-button>
				</div>
			</b-modal>

			<b-modal v-model="page.deletePractice.show" size="md" title="Confirm delete" no-close-on-backdrop no-close-on-esc hide-header-close>
				<error-list :errors="page.deletePractice.errors"></error-list>

				<p>
					Are you sure you want to delete this practice?
					This action is permanent and cannot be undone.
				</p>

				<div slot="modal-footer">
					<save-button type="button" :saving="page.deletePractice.saving" @click.native="confirmDeletePractice" text="Delete" variant="danger" />
					<b-button type="button" variant="secondary" @click="page.deletePractice.show = false" class="ml-1">Cancel</b-button>
				</div>
			</b-modal>

			<b-modal v-model="modal.show" title="Practice Information" scrollable size="lg" hide-footer>
				<error-list :errors="modal.errors"></error-list>

				<reimbursement-activity-form :item="modal.item" :dam="modal.dam" :practice="modal.practice" :is-update="modal.isUpdate" @change="modalSaved" @cancel="cancelModal"></reimbursement-activity-form>
			</b-modal>

			<b-modal v-model="files.delete.show" size="md" title="Confirm delete" no-close-on-backdrop no-close-on-esc hide-header-close>
				<error-list :errors="files.delete.errors"></error-list>

				<p>
					Are you sure you want to delete this file?
					This action is permanent and cannot be undone.
				</p>

				<div slot="modal-footer">
					<save-button type="button" :saving="files.delete.saving" @click.native="confirmDeleteFile" text="Delete" variant="danger" />
					<b-button type="button" variant="secondary" @click="files.delete.show = false" class="ml-1">Cancel</b-button>
				</div>
			</b-modal>
		</auth-container>
		<router-view></router-view>
	</div>	
</template>

<script>
	import GridView from '@/components/helpers/GridView';
	import ReimbursementForm from './ReimbursementForm.vue';
	import ReimbursementActivityForm from './ReimbursementActivityForm.vue';

	export default {
		name: 'ReimbursementEdit',
		components: {
			GridView, ReimbursementForm, ReimbursementActivityForm
		},
		data() {
			return {
				page: {
					errors: [],
					loading: false,
					showLogin: false,
					tabIndex: 0,
					delete: {
						show: false,
						errors: [],
						saving: false
					},
					deletePractice: {
						show: false,
						errors: [],
						saving: false,
						id: null
					}
				},
				item: {},
				sponsor: {},
				proposal: {},
				activities: {
					fields: [
						{ key: 'edit', label: '' },
						{ key: 'dam', label: 'Dam', sortable: true },
						{ key: 'practice', label: 'Practice', sortable: true },
						{ key: 'unitsPerformed', label: 'Units Performed', sortable: true, class: 'text-right', formatter: (value) => { return this.numberFormat(value, 2); } },
						{ key: 'unitCost', label: 'Unit Cost', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'invoicedAmount', label: 'Invoiced', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'stateAmount', label: 'State 90%', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'requiredMatch', label: 'Required Match', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'isInkindMatch', label: 'In-Kind Match?', sortable: true, class: 'text-center', formatter: (value) => { return value ? '✔' : '-'; } },
						{ key: 'inkindMatchDeduction', label: 'In-Kind Deduction', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'remainingRequiredMatch', label: 'Remaining Match Req.', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'totalOmPaid', label: 'Total O&M Paid', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'stateAdminPayment', label: 'State Admin. Paid', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'totalPaid', label: 'Total Paid', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'reappropriated', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
						{ key: 'notes', label: 'Notes', sortable: true },
						{ key: 'delete', label: '' }
					],
					items: []
				},
				table: {
					fields: [
						{ key: 'activityDate', label: 'Date', sortable: true, formatter: (value) => { return this.toDate(value, 'M/D/YYYY h:mm a') } },
						{ key: 'userName', label: 'User', sortable: true },
						{ key: 'description', label: 'Description', sortable: true }
					],
					sort: 'activityDate',
					reverse: true,
					itemsPerPage: 100
				},
				modal: {
					item: {},
					dam: null,
					practice: null,
					show: false,
					loading: false,
					errors: [],
					isUpdate: false
				},
				files: {
					items: [],
					errors: [],
					saving: false,
					fileInput: null,
					upload: {
						file: null,
						description: null
					},
					delete: {
						model: null,
						show: false,
						errors: []
					}
				}
			}
		},
		async created() {
			await this.get();
		},
		watch: {
			'$route': 'get'
		},
		methods: {
			async get() {
				this.page.loading = true;
				this.page.errors = [];

				try {
					const response = await this.$http.get(`omreimbursementrequests/${this.$route.params.id}`, this.getTokenHeader());
					this.log(response.data);
					this.item = response.data.request;
					this.sponsor = response.data.sponsor;
					this.proposal = response.data.proposal;
					this.activities.items = response.data.activities;
					this.files.items = response.data.files;
				} catch (error) {
					if (this.isApiUnauthorized(error)) this.page.showLogin = true;
					else this.page.errors = this.logError(error);
				}

				this.page.loading = false;
			},
			async changesSaved() {
				await this.get();
				this.page.tabIndex = 1;
				this.$refs.reimbursementForm.launchToast();
			},
			deletePractice(id) {
				this.page.deletePractice.id = id;
				this.page.deletePractice.show = true;
			},
			async confirmDeletePractice() {
				if (this.page.deletePractice.id !== null) {
					this.page.deletePractice.errors = [];
					this.page.deletePractice.saving = true;

					try {
						await this.$http.delete(`omreimbursementrequestactivities/${this.page.deletePractice.id}`, this.getTokenHeader());
						this.page.deletePractice.show = false;
						await this.get();
					} catch (error) {
						if (this.isApiUnauthorized(error)) this.page.showLogin = true;
						else this.page.deletePractice.errors = this.logError(error);
					}

					this.page.deletePractice.saving = false;
				}
			},
			async confirmDelete() {
				this.page.delete.errors = [];
				this.page.delete.saving = true;

				try {
					await this.$http.delete(`omreimbursementrequests/${this.$route.params.id}`, this.getTokenHeader());
					this.page.delete.show = false;
					this.$router.push({ name: 'ReimbursementList' });
				} catch (error) {
					if (this.isApiUnauthorized(error)) this.page.showLogin = true;
					else this.page.delete.errors = this.logError(error);
				}

				this.page.delete.saving = false;
			},
			async editActivity(id) {
				await this.getModal(id);
				this.modal.show = true;
			},
			createActivity() {
				this.resetModal();
				this.modal.show = true;
			},
			async modalSaved() {
				this.modal.show = false;
				this.resetModal();
				await this.get();
				this.$bvToast.show('practice-saved');
			},
			cancelModal() {
				this.modal.show = false;
				this.resetModal();
			},
			resetModal() {
				this.modal.item = {
					requestId: this.itemId,
					damId: null,
					practiceId: null,
					unitsPerformed: 0,
					unitCost: 0,
					isInkindMatch: false,
					inkindMatchDeduction: 0,
					totalOmPaid: 0,
					proposedCost: 0,
					notes: null
				}
				this.modal.dam = null;
				this.modal.practice = null;
				this.modal.isUpdate = false;
			},
			async getModal(id) {
				this.modal.loading = true;
				this.modal.errors = [];

				try {
					const response = await this.$http.get(`omreimbursementrequestactivities/${id}`, this.getTokenHeader());
					this.log(response.data);
					this.modal.item = response.data.activity;
					this.modal.dam = response.data.dam;
					this.modal.practice = response.data.practice;
					this.modal.isUpdate = true;
				} catch (error) {
					this.modal.errors = this.logError(error);
				}

				this.modal.loading = false;
			},
			selectFile(event) {
				this.files.upload.file = event.target.files[0];
			},
			async uploadFile() {
				this.files.saving = true;
				this.files.errors = [];
				if (this.files.upload.file === null) {
					this.files.errors.push('Please select a file to upload.');
				}
				else if (this.isNullOrEmpty(this.files.upload.description)) {
					this.files.errors.push('Please enter a label or short description of the file.');
				} else {
					try {
						let formData = new FormData();
						formData.append('file', this.files.upload.file);
						formData.append('description', this.files.upload.description);

						const response = await this.$http.post(`omreimbursementrequests/files/${this.$route.params.id}`, formData, this.getTokenHeaderMultiPart());
						this.log(response.data);
						this.files.upload.file = null;
						this.files.fileInput = null;
						this.files.upload.description = null;
						await this.get();
						this.page.tabIndex = 2;
					} catch (error) {
						this.files.errors = this.logError(error);
					}
				}


				this.files.saving = false;
			},
			deleteFile(file) {
				this.files.delete.model = file;
				this.files.delete.show = true;
			},
			async confirmDeleteFile() {
				this.files.delete.errors = [];
				this.files.delete.saving = true;

				try {
					await this.$http.put(`omreimbursementrequests/deletefile/${this.$route.params.id}`, this.files.delete.model, this.getTokenHeader());
					this.files.delete.show = false;
					await this.get();
					this.page.tabIndex = 2;
				} catch (error) {
					this.files.delete.errors = this.logError(error);
				}

				this.files.delete.saving = false;
			}
		},
		computed: {
			itemId() {
				return this.$route.params.id;
			}
		}
	}
</script>